/**
 * The password rules, as the server checks them (setStaffPasswordSchema in
 * server/http/validation/console.ts): PASSWORD_MIN_LENGTH to
 * PASSWORD_MAX_LENGTH characters, a letter and a character that is not a
 * letter, and not containing the email's name part (when that part has 4 or
 * more characters). Shown live so nobody learns them from a refusal.
 */
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "@wallyo/contracts/console";
import { t } from "../i18n";

export type PasswordRule = { id: "length" | "mix" | "email"; label: string; met: boolean };

/** The part of the email before `@`, lower-cased, when long enough to be a rule. */
export function emailNamePart(email: string): string | null {
  const name = email.trim().toLowerCase().split("@")[0] ?? "";
  return name.length >= 4 ? name : null;
}

export function passwordRules(email: string, password: string): PasswordRule[] {
  const name = emailNamePart(email);
  const tooLong = password.length > PASSWORD_MAX_LENGTH;
  return [
    {
      id: "length",
      label: tooLong
        ? t("setPassword.rule.tooLong", { max: PASSWORD_MAX_LENGTH })
        : t("setPassword.rule.length", { min: PASSWORD_MIN_LENGTH }),
      met: password.length >= PASSWORD_MIN_LENGTH && !tooLong,
    },
    {
      id: "mix",
      label: t("setPassword.rule.mix"),
      met: /\p{L}/u.test(password) && /[^\p{L}]/u.test(password),
    },
    {
      id: "email",
      label: name ? t("setPassword.rule.email", { name }) : t("setPassword.rule.emailGeneric"),
      met: password.length > 0 && (!name || !password.toLowerCase().includes(name)),
    },
  ];
}

export function passwordAcceptable(email: string, password: string): boolean {
  return passwordRules(email, password).every((rule) => rule.met);
}
